import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { RefreshTokenService } from '@app/services/refresh-token.service';
import {
  ApiUser,
  ApiUsersResponse,
  UserModel,
  mapApiUserToModel,
} from '@app/tpv/models/user.model';
import {
  StoreUsersExportFormat,
  StoreUsersExportPayload,
  StoreUsersRepository,
  StoreUsersSearchFilter,
} from '@app/domain/ports/store-users.repository';

/**
 * Implementación HTTP del port `StoreUsersRepository`. Centraliza las llamadas
 * a `/auth/users` y `/auth/downloadUsers` y aplica el filtrado client-side por
 * `id_merchant` (mientras el backend no honre el query param). Cuando el
 * backend filtre del lado servidor este `.filter(...)` será un no-op y se podrá
 * eliminar.
 */
@Injectable()
export class StoreUsersRemoteDataSource extends StoreUsersRepository {
  private readonly baseUrl = environment.graphqlApiUrl;
  private readonly http = inject(HttpClient);
  private readonly refreshTokenService = inject(RefreshTokenService);

  override getByMerchant(
    idMerchant: number,
    page = 1,
    limit = 100,
    filters?: StoreUsersSearchFilter
  ): Observable<UserModel[]> {
    const headers = this.buildHeaders();
    const params: Record<string, string> = {
      page: String(page),
      limit: String(limit),
      id_merchant: String(idMerchant),
    };

    const firstName = filters?.first_name?.trim();
    const lastName = filters?.last_name?.trim();
    const email = filters?.email?.trim();
    const status = filters?.status?.trim();
    const roleName = filters?.role_name?.trim();

    if (firstName) params['first_name'] = firstName;
    if (lastName) params['last_name'] = lastName;
    if (email) params['email'] = email;
    if (status) params['status'] = status;
    if (roleName) params['role_name'] = roleName;

    return this.http
      .get<ApiUsersResponse>(`${this.baseUrl}/auth/users`, { headers, params })
      .pipe(
        map((res) => {
          const root = res as unknown as Record<string, unknown>;
          const rawData = root['data'];
          const rows: ApiUser[] = Array.isArray(rawData) ? (rawData as ApiUser[]) : [];
          const filtered = rows.filter(
            (u) =>
              Array.isArray(u.merchants) &&
              u.merchants.some(
                (m) =>
                  typeof m === 'object' &&
                  m !== null &&
                  Number((m as { id_merchant?: unknown }).id_merchant) === idMerchant
              )
          );
          return filtered.map(mapApiUserToModel);
        })
      );
  }

  override downloadExport(
    format: StoreUsersExportFormat
  ): Observable<StoreUsersExportPayload | null> {
    const headers = this.buildHeaders();

    return this.http
      .get<Record<string, unknown>>(`${this.baseUrl}/auth/downloadUsers`, {
        headers,
        params: { format },
      })
      .pipe(
        map((response): StoreUsersExportPayload | null => {
          const root = response ?? {};
          const nested = root['data'] as Record<string, unknown> | undefined;

          const base64 =
            (root['file_base64'] as string | undefined) ??
            (root['fileBase64'] as string | undefined) ??
            (nested?.['file_base64'] as string | undefined) ??
            (nested?.['fileBase64'] as string | undefined);

          if (!base64 || typeof base64 !== 'string') {
            return null;
          }

          const mimeType =
            format === 'excel'
              ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              : format === 'csv'
                ? 'text/csv;charset=utf-8'
                : 'application/pdf';

          const fileName =
            (root['file_name'] as string | undefined) ??
            (root['fileName'] as string | undefined) ??
            (nested?.['file_name'] as string | undefined) ??
            (nested?.['fileName'] as string | undefined) ??
            `users.${format === 'excel' ? 'xlsx' : format}`;

          return { base64, fileName: String(fileName), mimeType };
        }),
        catchError(() => of<StoreUsersExportPayload | null>(null))
      );
  }

  /**
   * Mantiene el contrato de `UserService.buildHeaders` (x-api-key + Bearer +
   * Skip-Auth-Interceptor) para no alterar el comportamiento mientras existan
   * los otros consumidores; la migración del header monolítico es deuda
   * trackeada en CLAUDE.md (BFF / decomposición de auth.interceptor).
   */
  private buildHeaders(): HttpHeaders {
    const token = this.refreshTokenService.getAccessToken();
    return new HttpHeaders({
      'x-api-key': environment.API_KEY,
      'Content-Type': 'application/json',
      'Skip-Auth-Interceptor': 'true',
      ...(token && { Authorization: `Bearer ${token}` }),
    });
  }
}
